import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  AudioLines,
  CalendarCheck,
  MessageSquareText,
  Mic,
  PhoneOff,
  RotateCcw,
  Send,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { auraApi } from "../../lib/aura";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { AVATAR_VIDEO_ID, useAura } from "./AuraProvider";
import FollowUps from "./FollowUps";
import { LanguageChips } from "./LanguageToggle";
import MessageActions from "./MessageActions";
import OpportunityFinder from "./OpportunityFinder";
import SimmiCard from "./SimmiCard";
import SimmiMarkdown from "./SimmiMarkdown";
import SimmiStatus from "./SimmiStatus";
import { useDictation } from "./useDictation";

// Kinds of Simmi lines that aren't answers, so they get no copy / 👍 / 👎.
const NO_REACTIONS = new Set(["voice", "nudge", "end", "clarify"]);
export const canReact = (m) => m.role === "assistant" && m.id !== "greeting" && !NO_REACTIONS.has(m.kind) && !m.error && m.text;
export const followupsOf = (m) => m?.actions?.find((a) => a.type === "followups")?.items || [];

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
const SHORTCUT = IS_MAC ? "⌘K" : "Ctrl K";

export const AuraOrb = ({ size = 40, state = "idle" }) => (
  <span
    aria-hidden
    className={`aura-orb aura-orb--${state} inline-block shrink-0 rounded-full`}
    style={{ width: size, height: size }}
  />
);

export const CALL_STATUS = {
  idle: "Connecting…",
  listening: "Listening…",
  thinking: "Thinking…",
  speaking: "Simmi is speaking",
};

const MeetingCard = () => {
  const { config, recordEvent, meetingBooked, leadCaptured, sessionId, setMeetingBooked } = useAura();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_time: "" });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [error, setError] = useState("");

  // Calendly posts this message from its embed when a booking completes.
  useEffect(() => {
    if (!dialogOpen) return undefined;
    const onMessage = (e) => {
      if (e.data?.event === "calendly.event_scheduled") {
        recordEvent({ type: "meeting_booked" }).catch(() => {});
        setDialogOpen(false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [dialogOpen, recordEvent]);

  if (meetingBooked) {
    return (
      <div className="flex items-center gap-2 rounded-[12px] border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-[13px] text-emerald-200">
        <CalendarCheck className="h-4 w-4" /> Meeting booked — the team will come prepared with this conversation.
      </div>
    );
  }

  if (config.booking_url) {
    return (
      <>
        <button
          onClick={() => setDialogOpen(true)}
          className="flex w-full items-center justify-between rounded-[12px] border border-[#2563EB]/50 bg-[#2563EB]/15 px-4 py-3 text-left transition-colors hover:bg-[#2563EB]/25"
        >
          <span className="flex flex-col">
            <span className="text-[14px] font-semibold text-white">Book time with an AI strategist</span>
            <span className="text-[12px] text-white/60">30-minute call · pick a slot that works</span>
          </span>
          <CalendarCheck className="h-5 w-5 text-[#06D3DB]" />
        </button>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-[760px] p-0">
            <DialogHeader className="px-6 pt-5">
              <DialogTitle>Book time with Simpel</DialogTitle>
            </DialogHeader>
            <iframe title="Book a meeting" src={config.booking_url} className="h-[70vh] w-full rounded-b-lg border-0" />
            <a href={config.booking_url} target="_blank" rel="noreferrer" className="px-6 pb-4 text-[12px] text-slate-500 underline">
              Open in a new tab
            </a>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Book a meeting without the avatar: name, email and phone, saved like the persona's book_meeting tool does.
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const result = await auraApi.bookMeeting({ ...form, channel: "form", page: window.location.pathname, session_id: sessionId || "" });
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      setSavedId(result.id);
      setMeetingBooked(true);
      recordEvent({ type: "email_submitted", email: form.email.trim() }).catch(() => {}); // keeps the lead too
    } catch {
      setError("Couldn't save that just now. Please try again, or email contact@simpel.ai.");
    } finally {
      setSaving(false);
    }
  };

  if (savedId || leadCaptured) {
    return (
      <div className="rounded-[12px] border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-[13px] text-emerald-200">
        Thanks, you're booked in. Our team will contact you to confirm a time.
      </div>
    );
  }
  const field = (key, props) => (
    <input
      value={form[key]}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      className="w-full rounded-[8px] bg-white px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#06D3DB]"
      {...props}
    />
  );
  return (
    <form onSubmit={submit} className="flex w-full flex-col gap-2 rounded-[12px] border border-[#2563EB]/50 bg-[#2563EB]/15 p-3 text-left">
      <p className="text-[13px] font-semibold text-white">Book a meeting with our team</p>
      {field("name", { required: true, placeholder: "Your name", autoComplete: "name", "aria-label": "Your name" })}
      {field("email", { required: true, type: "email", placeholder: "Work email", autoComplete: "email", "aria-label": "Work email" })}
      {field("phone", { required: true, type: "tel", placeholder: "Phone number", autoComplete: "tel", "aria-label": "Phone number" })}
      {field("preferred_time", { placeholder: "When suits you? (optional)", "aria-label": "Preferred time" })}
      <button
        type="submit"
        disabled={saving}
        className="rounded-[8px] bg-[#2563EB] px-3 py-2 text-[13px] font-medium text-white hover:bg-[#1d4ed8] disabled:opacity-60"
      >
        {saving ? "Booking…" : "Book meeting"}
      </button>
      {error && <p className="text-[12px] text-rose-300">{error}</p>}
      <p className="text-[11px] text-white/50">We use these details only to arrange the meeting.</p>
    </form>
  );
};

// One thing under Simmi's reply. Follow-up chips aren't here: they render after all of a reply's actions.
export const Action = ({ action, onNavigate, message }) => {
  if (action.type === "book_meeting") return <MeetingCard />;
  if (action.type === "card") return <SimmiCard action={action} onNavigate={onNavigate} />;
  if (action.type === "opportunity_finder") return message ? <OpportunityFinder message={message} /> : null;
  if (action.type === "language_picker") return <LanguageChips />;
  if (action.type === "open_page" && action.blurb) return <SimmiCard action={action} onNavigate={onNavigate} />;
  if (action.type === "open_page") {
    return (
      <button
        onClick={() => onNavigate(action.path)}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] font-medium text-white/90 transition-colors hover:bg-white/10"
      >
        Open {action.label} <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    );
  }
  return null;
};

// Hands-free voice conversation: a large living orb, status, live captions.
const CallControls = ({ endCall }) => (
  <div className="flex items-center gap-3">
    <button
      onClick={endCall}
      className="flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-2.5 text-[13px] font-medium text-white/90 backdrop-blur transition-colors hover:bg-white/10"
    >
      <MessageSquareText className="h-4 w-4" /> Switch to chat
    </button>
    <button
      onClick={endCall}
      aria-label="End voice conversation"
      className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-500 text-white transition-transform hover:scale-105"
    >
      <PhoneOff className="h-5 w-5" />
    </button>
  </div>
);

// Docket-style talking avatar (Anam): full-bleed video, status chip, captions over the picture.
const AvatarView = ({ onNavigate }) => {
  const { callState, caption, endCall, messages } = useAura();
  const last = [...messages].reverse().find((m) => m.role === "assistant");
  const actions = callState !== "listening" ? last?.actions || [] : [];
  return (
    <div className="relative flex-1 overflow-hidden bg-black">
      <video id={AVATAR_VIDEO_ID} autoPlay playsInline className="absolute inset-0 h-full w-full object-cover" />
      {callState === "idle" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#050B15]">
          <AuraOrb size={90} state="thinking" />
          <p className="text-[12px] font-semibold uppercase tracking-[2px] text-[#06D3DB]">Connecting to Simmi…</p>
        </div>
      )}
      <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
        <span className={`h-1.5 w-1.5 rounded-full ${callState === "listening" ? "animate-pulse bg-emerald-400" : "bg-[#06D3DB]"}`} />
        {CALL_STATUS[callState]}
      </span>
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-4 pb-4 pt-16 text-center">
        {caption && <p className="max-w-[420px] text-[14px] leading-[21px] text-white">{caption}</p>}
        {actions.length > 0 && (
          <div className="flex w-full max-w-[340px] flex-col items-center gap-2">
            {actions.map((a, i) => (
              <Action key={i} action={a} onNavigate={onNavigate} message={last} />
            ))}
          </div>
        )}
        <CallControls endCall={endCall} />
      </div>
    </div>
  );
};

const CallView = ({ big, onNavigate }) => {
  const { callState, caption, endCall, messages, avatarMode } = useAura();
  const last = [...messages].reverse().find((m) => m.role === "assistant");
  const actions = callState !== "listening" ? last?.actions || [] : [];
  if (avatarMode) return <AvatarView onNavigate={onNavigate} />;
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-6 py-6 text-center">
      <div className={`aura-call aura-call--${callState}`}>
        <AuraOrb size={big ? 150 : 120} state={callState} />
      </div>
      <p className="text-[12px] font-semibold uppercase tracking-[2px] text-[#06D3DB]" aria-live="polite">
        {CALL_STATUS[callState]}
      </p>
      <p className="min-h-[66px] max-w-[380px] text-[15px] leading-[22px] text-white/85">
        {caption || (callState === "listening" ? "Go ahead, I'm listening." : "")}
      </p>
      {actions.length > 0 && (
        <div className="flex w-full max-w-[340px] flex-col items-center gap-2">
          {actions.map((a, i) => (
            <Action key={i} action={a} onNavigate={onNavigate} message={last} />
          ))}
        </div>
      )}
      <div className="pt-1">
        <CallControls endCall={endCall} />
      </div>
    </div>
  );
};

const AuraChat = ({ variant = "floating", onClose }) => {
  const {
    messages,
    sending,
    send,
    voiceOn,
    setVoiceOn,
    config,
    hasConversation,
    reset,
    callActive: anyCallActive,
    callSurface,
    startCall,
    canTalk,
    status,
    lang,
    t,
  } = useAura();
  // A call started from the frameless hero plays there; this panel just shows the transcript.
  const callActive = anyCallActive && callSurface === "panel";
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const dictation = useDictation({ draft, setDraft, lang, disabled: anyCallActive });

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, status, callActive]);

  // Ctrl/⌘+K (see AuraProvider) puts the cursor here.
  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    window.addEventListener("simmi:focus", focus);
    return () => window.removeEventListener("simmi:focus", focus);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    dictation.stop();
    send(draft);
    setDraft("");
  };

  const toggleVoice = () => {
    if (voiceOn) window.speechSynthesis?.cancel();
    setVoiceOn(!voiceOn);
  };

  const goTo = (path) => {
    navigate(path);
    window.scrollTo({ top: 0 });
  };

  const isHero = variant === "hero";
  const isSheet = variant === "sheet"; // phones: fills the bottom sheet (AuraLauncher)
  const last = messages[messages.length - 1];
  const lastId = last?.id;
  const chips = !sending && last?.role === "assistant" && last.kind !== "end" ? followupsOf(last) : [];
  const size = isHero
    ? "h-[560px] w-full rounded-[22px] border"
    : isSheet
      ? "h-full w-full rounded-t-[22px] border-t"
      : "h-[min(620px,calc(100vh-120px))] w-[min(400px,calc(100vw-32px))] rounded-[22px] border";
  return (
    <div
      className={`aura-panel flex flex-col overflow-hidden border-[#334155]/70 text-white ${size}`}
      role="region"
      aria-label="Conversation with Simmi"
      data-simmi
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
        <AuraOrb size={38} state={sending ? "thinking" : "idle"} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[15px] font-semibold leading-5">Simmi</span>
          <span className="flex items-center gap-1.5 text-[11px] text-white/60">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
            Simpel AI agent{config.mode === "offline" ? " · offline mode" : ""}
          </span>
        </div>
        {canTalk && !anyCallActive && (
          <button
            onClick={() => startCall("panel")}
            className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-white/15"
          >
            <AudioLines className="h-3.5 w-3.5 text-[#06D3DB]" /> Talk
          </button>
        )}
        {hasConversation && !callActive && (
          <button
            onClick={reset}
            aria-label="Start a new chat"
            title="New chat"
            className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
        {!callActive && (
          <button
            onClick={toggleVoice}
            aria-label={voiceOn ? "Mute voice replies" : "Read replies aloud"}
            title={voiceOn ? "Mute voice replies" : "Read replies aloud"}
            className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white"
          >
            {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        )}
        {onClose && (
          <button onClick={onClose} aria-label="Close Simmi" className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {callActive ? (
        <CallView big={isHero} onNavigate={goTo} />
      ) : (
        <>
          {/* Messages */}
          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`aura-msg group/msg flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`}
              >
                {m.text && (
                  <div
                    className={`max-w-[85%] rounded-[16px] px-3.5 py-2.5 text-[14px] leading-[21px] ${
                      m.role === "user"
                        ? "whitespace-pre-wrap rounded-br-[4px] bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white"
                        : m.kind === "end"
                          ? "rounded-bl-[4px] border border-white/10 bg-transparent text-white/60"
                          : "rounded-bl-[4px] bg-white/[0.07] text-white/90"
                    }`}
                  >
                    {m.role === "user" ? m.text : <SimmiMarkdown text={m.text} onNavigate={goTo} />}
                  </div>
                )}
                {m.actions?.some((a) => a.type !== "followups") && !(sending && m.id === lastId) && (
                  <div className="flex w-full max-w-[85%] flex-col items-start gap-2">
                    {m.actions.map((a, i) => (
                      <Action key={i} action={a} onNavigate={goTo} message={m} />
                    ))}
                  </div>
                )}
                {canReact(m) && !(sending && m.id === lastId) && (
                  // Always visible on touch screens; on desktop they appear on hover or keyboard focus.
                  <MessageActions
                    message={m}
                    className="-mt-1 opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/msg:opacity-100 [@media(hover:hover)]:focus-within:opacity-100"
                  />
                )}
                {m.kind === "end" && m.id === lastId && (
                  <div className="flex gap-2">
                    {canTalk && (
                      <button
                        onClick={startCall}
                        className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-medium hover:bg-white/15"
                      >
                        <AudioLines className="h-3.5 w-3.5 text-[#06D3DB]" /> Talk to Simmi again
                      </button>
                    )}
                    <button onClick={reset} className="rounded-full border border-white/15 px-3 py-1.5 text-[12px] text-white/80 hover:bg-white/10">
                      New chat
                    </button>
                  </div>
                )}
              </div>
            ))}
            {sending && status && (
              <div className="px-1">
                <SimmiStatus statusKey={status} t={t} />
              </div>
            )}
            <FollowUps items={chips} className="max-w-[92%] pt-0.5" />
            {!hasConversation && (
              <div className="flex flex-wrap gap-2 pt-1">
                {t.starters.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="rounded-full border border-white/15 px-3 py-1.5 text-[12px] text-white/80 transition-colors hover:border-[#06D3DB]/60 hover:text-white"
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Composer */}
          {dictation.denied && <p className="px-4 pt-2 text-[12px] text-rose-300">{t.micDenied}</p>}
          <form
            onSubmit={submit}
            className="flex items-center gap-2 border-t border-white/10 px-3 pt-3"
            style={{ paddingBottom: isSheet ? "max(12px, env(safe-area-inset-bottom))" : 12 }}
          >
            {dictation.supported && (
              // Dictation into the box (hold to talk, or tap to start and stop); "Talk" in the header starts a call.
              <button
                type="button"
                {...dictation.buttonProps}
                aria-label={t.dictate}
                title={t.dictate}
                disabled={anyCallActive}
                className={`relative shrink-0 touch-none select-none rounded-full p-2.5 transition-colors disabled:opacity-40 ${
                  dictation.recording ? "bg-rose-500/20 text-rose-300" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                {dictation.recording && <span className="absolute inset-0 animate-ping rounded-full border border-rose-400/70" />}
                <Mic className="relative h-4 w-4" />
              </button>
            )}
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={dictation.recording ? t.dictating : t.askAnything}
              maxLength={2000}
              className="min-w-0 flex-1 bg-transparent px-1 text-[14px] text-white placeholder:text-white/40 focus:outline-none"
            />
            {!draft && !isSheet && (
              <kbd className="hidden shrink-0 rounded-[6px] border border-white/10 px-1.5 py-0.5 font-sans text-[10.5px] text-white/35 md:inline">
                {SHORTCUT}
              </kbd>
            )}
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              aria-label="Send"
              className="rounded-full bg-[#2563EB] p-2.5 text-white transition-opacity hover:bg-[#1d4ed8] disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </>
      )}
    </div>
  );
};

// Docket-style "frameless" entry point that lives inside the hero copy.
export const AuraHeroPrompt = () => {
  const { send, setHeroVisible, hasConversation, sending, startCall, canTalk, t } = useAura();
  const [draft, setDraft] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    // Watch the whole hero (prompt + embedded chat), so the floating launcher never duplicates it.
    const el = ref.current?.closest("section") || ref.current;
    if (!el || !window.IntersectionObserver) return undefined;
    const io = new IntersectionObserver(([entry]) => setHeroVisible(entry.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => {
      io.disconnect();
      setHeroVisible(false);
    };
  }, [setHeroVisible]);

  const submit = (e) => {
    e.preventDefault();
    send(draft);
    setDraft("");
  };

  return (
    <div ref={ref} className="flex w-full max-w-[560px] flex-col gap-3">
      <form
        onSubmit={submit}
        className="flex items-center gap-3 rounded-full border border-[#334155] bg-white/[0.06] py-2 pl-2 pr-2 backdrop-blur"
        style={{ boxShadow: "0 0 32px rgba(6,211,219,0.15)" }}
      >
        <AuraOrb size={36} state={sending ? "thinking" : "idle"} />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask Simmi how AI could work for your business…"
          aria-label="Ask Simmi"
          className="min-w-0 flex-1 bg-transparent text-[15px] text-white placeholder:text-white/50 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          aria-label="Ask"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-black transition-transform hover:scale-105 disabled:opacity-40"
        >
          <ArrowRight className="h-4 w-4" />
        </button>
      </form>
      {!hasConversation && (
        <div className="flex flex-wrap gap-2">
          {canTalk && (
            <button
              onClick={startCall}
              className="aura-talk-btn flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-white"
            >
              <AudioLines className="h-3.5 w-3.5" /> Talk to Simmi
            </button>
          )}
          {t.starters.slice(0, 2).map((p) => (
            <button
              key={p}
              onClick={() => send(p)}
              className="rounded-full border border-white/15 bg-white/[0.03] px-3 py-1.5 text-[12px] text-white/75 transition-colors hover:border-[#06D3DB]/60 hover:text-white"
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuraChat;
